import { ReactNode } from 'react';
import { styled } from '@mui/material/styles';
import { QButton, QIconButton, QModal, QModalActions, QModalContent, QTypography } from '@kw/quokka-ui';

interface OrganizationSettingsDialogProps {
  id: string;
  open: boolean;
  title: string;
  body?: ReactNode;
  children?: ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  onClose: () => void;
  showCancel?: boolean;
  isSaving?: boolean;
  primaryDisabled?: boolean;
}

export const OrganizationSettingsDialog = ({
  id,
  open,
  title,
  body,
  children,
  primaryLabel,
  onPrimary,
  onClose,
  showCancel = false,
  isSaving = false,
  primaryDisabled = false,
}: OrganizationSettingsDialogProps) => {
  // QModal leaves its outer Paper in the document when closed. Rendering nothing
  // prevents that empty element from becoming an extra item in card layouts.
  if (!open) return null;

  const titleId = `${id}-title`;

  return (
    <QModal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth={false}
      PaperProps={dialogPaperProps}
      title={
        <DialogHeader>
          <DialogTitle id={titleId} variant="h3Medium">
            {title}
          </DialogTitle>
        </DialogHeader>
      }
      content={
        <DialogContent>
          {body && <DialogDescription variant="bodyRegular">{body}</DialogDescription>}
          {children}
        </DialogContent>
      }
      actions={
        <DialogActions>
          {showCancel && (
            <QButton variant="outlined" onClick={onClose} disabled={isSaving}>
              Cancel
            </QButton>
          )}
          <QButton variant="filled" onClick={onPrimary} disabled={isSaving || primaryDisabled}>
            {isSaving ? 'Saving…' : primaryLabel}
          </QButton>
        </DialogActions>
      }
    />
  );
};

const dialogPaperProps = {
  sx: {
    width: '568px',
    minWidth: 0,
    maxWidth: 'calc(100vw - 48px)',
    borderRadius: '12px',
    overflow: 'hidden',
    '& .MuiDivider-root': {
      display: 'none',
    },
  },
};

const DialogHeader = styled('div')`
  padding: 26px 28px 0;
`;

const DialogTitle = styled(QTypography)`
  display: block;
  font-size: 18px;
  line-height: 25px;
  font-weight: 700;
`;

const DialogContent = styled(QModalContent)`
  padding: 10px 28px 0;
`;

const DialogDescription = styled(QTypography)`
  display: block;
  color: #52666d;
  font-size: 14px;
  line-height: 22px;
`;

const DialogActions = styled(QModalActions)`
  gap: 10px;
  padding: 24px 28px 22px;

  button {
    min-width: 90px;
  }
`;

export const OrganizationSettingsFieldLabel = styled('label')`
  display: block;
  margin: 19px 0 8px;
  color: #66777d;
  font-size: 11px;
  line-height: 16px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const OrganizationSettingsValidationMessage = styled('div')`
  margin-top: 8px;
  color: #df2e21;
  font-size: 13px;
  line-height: 18px;
`;

export const OrganizationSettingsSnackbarDismissButton = styled(QIconButton)`
  color: #ffffff;
`;
